import React from 'react';

import { type Control, Controller, type UseFieldArrayAppend, type UseFieldArrayRemove } from 'react-hook-form';
import { type FieldArrayWithId } from 'react-hook-form';

import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import type { ParityItemInput } from '~/types/services/parityNotifications';

import type { FormValues } from './types';

const EMPTY_PARITY_ITEM: ParityItemInput = {
  week_start: '',
  week_end: '',
  competitor: '',
  amount: 0,
  competitor_amount: 0,
};

interface ParityItemsTableProps {
  control: Control<FormValues>;
  isSubmitting: boolean;
  fields: FieldArrayWithId<FormValues, 'parityItems', 'id'>[];
  append: UseFieldArrayAppend<FormValues, 'parityItems'>;
  remove: UseFieldArrayRemove;
}

export default function ParityItemsTable({ control, isSubmitting, fields, append, remove }: ParityItemsTableProps) {
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="subtitle1" fontWeight="medium">
          Weekly Parity Items
        </Typography>
        <Button
          variant="text"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => append({ ...EMPTY_PARITY_ITEM })}
          disabled={isSubmitting}
        >
          Add Week
        </Button>
      </Stack>

      {fields.length > 0 && (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Week Start</TableCell>
                <TableCell>Week End</TableCell>
                <TableCell>Competitor</TableCell>
                <TableCell>LE Price (nightly)</TableCell>
                <TableCell>Competitor Price</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {fields.map((field, index) => (
                <TableRow key={field.id}>
                  <TableCell sx={{ minWidth: 160 }}>
                    <Controller
                      name={`parityItems.${index}.week_start`}
                      control={control}
                      rules={{ required: 'Required' }}
                      render={({ field: f, fieldState }) => (
                        <TextField
                          {...f}
                          type="date"
                          size="small"
                          disabled={isSubmitting}
                          error={!!fieldState.error}
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                        />
                      )}
                    />
                  </TableCell>
                  <TableCell sx={{ minWidth: 160 }}>
                    <Controller
                      name={`parityItems.${index}.week_end`}
                      control={control}
                      rules={{ required: 'Required' }}
                      render={({ field: f, fieldState }) => (
                        <TextField
                          {...f}
                          type="date"
                          size="small"
                          disabled={isSubmitting}
                          error={!!fieldState.error}
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                        />
                      )}
                    />
                  </TableCell>
                  <TableCell sx={{ minWidth: 180 }}>
                    <Controller
                      name={`parityItems.${index}.competitor`}
                      control={control}
                      rules={{ required: 'Required' }}
                      render={({ field: f, fieldState }) => (
                        <TextField
                          {...f}
                          size="small"
                          placeholder="e.g. Booking.com"
                          disabled={isSubmitting}
                          error={!!fieldState.error}
                          fullWidth
                        />
                      )}
                    />
                  </TableCell>
                  <TableCell sx={{ minWidth: 150 }}>
                    <Controller
                      name={`parityItems.${index}.amount`}
                      control={control}
                      rules={{ required: 'Required', min: { value: 0, message: '≥ 0' } }}
                      render={({ field: f, fieldState }) => (
                        <TextField
                          {...f}
                          onChange={(e) => f.onChange(Number(e.target.value))}
                          type="number"
                          size="small"
                          disabled={isSubmitting}
                          error={!!fieldState.error}
                          fullWidth
                          InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                          }}
                        />
                      )}
                    />
                  </TableCell>
                  <TableCell sx={{ minWidth: 150 }}>
                    <Controller
                      name={`parityItems.${index}.competitor_amount`}
                      control={control}
                      rules={{ required: 'Required', min: { value: 0, message: '≥ 0' } }}
                      render={({ field: f, fieldState }) => (
                        <TextField
                          {...f}
                          onChange={(e) => f.onChange(Number(e.target.value))}
                          type="number"
                          size="small"
                          disabled={isSubmitting}
                          error={!!fieldState.error}
                          fullWidth
                          InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                          }}
                        />
                      )}
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Remove row">
                      <IconButton size="small" onClick={() => remove(index)} disabled={isSubmitting}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
}
